import * as FSPR from "src/generated/hooks/form-service-public/requests";

export type AvailabilityFilters = {
  search: string;
  gradeLabel: string;
  programLabel: string;
  onlyAvailable: boolean;
};

export const EMPTY_FILTERS: AvailabilityFilters = {
  search: "",
  gradeLabel: "",
  programLabel: "",
  onlyAvailable: false,
};

export function hasActiveFilters(filters: AvailabilityFilters): boolean {
  return (
    filters.search !== "" ||
    filters.gradeLabel !== "" ||
    filters.programLabel !== "" ||
    filters.onlyAvailable
  );
}

/**
 * Grade and program options come from the rows themselves rather than a fixed
 * list so families are never offered a filter that matches nothing.
 */
export function uniqueSortedValues(
  rows: FSPR.PublicCapacityRow[],
  select: (row: FSPR.PublicCapacityRow) => string | undefined
): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const value = select(row);
    if (value !== undefined && value !== "") values.add(value);
  }
  return [...values].sort((a, b) => a.localeCompare(b));
}

/**
 * Cached / CDN responses written before gradeOrder shipped omit the field for
 * up to an hour. Those payloads are already in grade_config.order, so first
 * appearance is the fallback sort key.
 */
export function gradeOrderOf(row: FSPR.PublicCapacityRow): number | undefined {
  return typeof row.gradeOrder === "number" ? row.gradeOrder : undefined;
}

/**
 * Grade labels follow grade_config.order, the same numeric sequence used
 * everywhere else in the product. Deduping by label keeps the lowest order
 * when two configs share a display name.
 */
export function uniqueSortedGradeLabels(
  rows: FSPR.PublicCapacityRow[]
): string[] {
  const firstOrder = new Map<string, number>();
  for (const row of rows) {
    if (row.gradeLabel === "") continue;
    const existing = firstOrder.get(row.gradeLabel);
    const order = gradeOrderOf(row) ?? firstOrder.size;
    if (existing === undefined || order < existing) {
      firstOrder.set(row.gradeLabel, order);
    }
  }
  return [...firstOrder.entries()]
    .sort(([labelA, orderA], [labelB, orderB]) =>
      orderA !== orderB ? orderA - orderB : labelA.localeCompare(labelB)
    )
    .map(([label]) => label);
}

/**
 * The public legend is ordered most-available first, so the last entry is the
 * catch-all "no seats" band. "Show only available seats" excludes that band.
 */
export function unavailableSeatBandKey(
  seatLegend: FSPR.AvailabilityBandValue[]
): string | undefined {
  return seatLegend[seatLegend.length - 1]?.key;
}

export function applyFilters(
  rows: FSPR.PublicCapacityRow[],
  filters: AvailabilityFilters,
  seatLegend: FSPR.AvailabilityBandValue[] = []
): FSPR.PublicCapacityRow[] {
  const search = filters.search.trim().toLowerCase();
  const unavailableKey = filters.onlyAvailable
    ? unavailableSeatBandKey(seatLegend)
    : undefined;

  return rows.filter((row) => {
    if (search !== "" && !row.schoolName.toLowerCase().includes(search)) {
      return false;
    }
    if (filters.gradeLabel !== "" && row.gradeLabel !== filters.gradeLabel) {
      return false;
    }
    if (
      filters.programLabel !== "" &&
      (row.programLabel ?? "") !== filters.programLabel
    ) {
      return false;
    }
    if (unavailableKey !== undefined && row.seats.key === unavailableKey) {
      return false;
    }
    return true;
  });
}
